import DependentInput from 'elements/input/DependentInput'
import { Grid, Typography } from 'opensolar-ui'
import React, { useEffect, useMemo } from 'react'
import { BooleanInput, ChipsInput, NumberInput, ReferenceArrayInput, SelectInput, useTranslate } from 'react-admin'
import { useForm, useFormState } from 'react-final-form'
import { useStyles } from '../styles'
import {
  AU_FALLBACK_WHOLESALE_YEAR,
  WHOLESALE_COUNTRY_CHOICES,
  WHOLESALE_SPOT_SERIES_CALENDAR,
  WHOLESALE_TOGGLE_LABEL_DEFAULT,
  isCalendarWholesaleSpotSeries,
  normalizeWholesaleCountryIso2,
} from '../wholesaleTariffTypes'
import { useWholesaleTariffDerivedFromTariffData } from '../useWholesaleTariffDerivedFromTariffData'

type WholesaleTariffFieldsProps = {
  disabled?: boolean
}

function networkTariffGroupOptionText(
  networkTariffGroup:
    | {
        id?: number
        code?: string
        name?: string
        distributor_name?: string
        country_iso2?: string
      }
    | null
    | undefined
): string {
  if (networkTariffGroup == null) {
    return ''
  }
  const distributorAndCodeLabel = [networkTariffGroup.distributor_name, networkTariffGroup.code]
    .map((labelPart) => (labelPart != null && String(labelPart).trim() !== '' ? String(labelPart).trim() : null))
    .filter(Boolean)
    .join(' : ')
  if (distributorAndCodeLabel) {
    return networkTariffGroup.country_iso2
      ? `${distributorAndCodeLabel} (${networkTariffGroup.country_iso2})`
      : distributorAndCodeLabel
  }
  if (networkTariffGroup.name != null && String(networkTariffGroup.name).trim() !== '') {
    return String(networkTariffGroup.name).trim()
  }
  return networkTariffGroup.id != null ? String(networkTariffGroup.id) : ''
}

function resolveShowAuWholesaleSpotSeries(wholesaleCountryIso2: unknown): boolean {
  return normalizeWholesaleCountryIso2(wholesaleCountryIso2) === 'AU'
}

function resolveShowWholesalePriceYear(wholesaleCountryIso2: unknown, wholesaleSpotSeries: unknown): boolean {
  const country = normalizeWholesaleCountryIso2(wholesaleCountryIso2)
  if (country !== 'AU') {
    return true
  }
  return isCalendarWholesaleSpotSeries(wholesaleSpotSeries)
}

const WholesaleTariffFields: React.FC<WholesaleTariffFieldsProps> = ({ disabled }) => {
  const translate = useTranslate()
  const classes = useStyles()
  const form = useForm()
  const { values } = useFormState({ subscription: { values: true } })

  const tariffData = values?.tariffData
  const {
    wholesaleEnabled,
    wholesaleCountryIso2,
    wholesaleSpotSeriesChoices,
    wholesaleCountrySpotConfig,
    wholesalePriceYearChoices,
  } = useWholesaleTariffDerivedFromTariffData(tariffData)

  const networkTariffGroupListFilter = useMemo(() => ({ country__iso2: wholesaleCountryIso2 }), [wholesaleCountryIso2])

  const translatedSpotSeriesChoices = useMemo(
    () => wholesaleSpotSeriesChoices.map((choice) => ({ id: choice.id, name: translate(choice.name) })),
    [wholesaleSpotSeriesChoices, translate]
  )

  const translatedCountryChoices = useMemo(
    () => WHOLESALE_COUNTRY_CHOICES.map((choice) => ({ id: choice.id, name: translate(choice.name) })),
    [translate]
  )

  useEffect(() => {
    if (!wholesaleEnabled) {
      return
    }
    const marginalPriceLimits = tariffData?.wholesale_marginal_price_limits
    if (marginalPriceLimits != null && typeof marginalPriceLimits === 'object') {
      return
    }
    form.change('tariffData.wholesale_marginal_price_limits', {})
  }, [wholesaleEnabled, tariffData?.wholesale_marginal_price_limits, form])

  useEffect(() => {
    if (!wholesaleEnabled || wholesaleCountryIso2 !== 'AU') {
      return
    }
    const currentSeries = tariffData?.wholesale_spot_series
    const needsDefault = currentSeries === undefined || currentSeries === null || currentSeries === ''
    if (needsDefault) {
      form.change('tariffData.wholesale_spot_series', WHOLESALE_SPOT_SERIES_CALENDAR)
    }
  }, [wholesaleEnabled, wholesaleCountryIso2, tariffData?.wholesale_spot_series, form])

  useEffect(() => {
    if (!wholesaleEnabled) {
      return
    }
    const year = tariffData?.wholesale_year
    const numeric = Number(year)
    const invalid = year === undefined || year === null || year === '' || Number.isNaN(numeric)
    if (!invalid) {
      return
    }
    form.change('tariffData.wholesale_year', AU_FALLBACK_WHOLESALE_YEAR)
  }, [wholesaleEnabled, tariffData?.wholesale_year, form])

  useEffect(() => {
    if (!wholesaleEnabled || wholesaleCountryIso2 !== 'AU') {
      return
    }
    if (isCalendarWholesaleSpotSeries(tariffData?.wholesale_spot_series)) {
      return
    }
    if (tariffData?.wholesale_year !== AU_FALLBACK_WHOLESALE_YEAR) {
      form.change('tariffData.wholesale_year', AU_FALLBACK_WHOLESALE_YEAR)
    }
  }, [
    wholesaleEnabled,
    wholesaleCountryIso2,
    tariffData?.wholesale_spot_series,
    tariffData?.wholesale_year,
    form,
  ])

  const wholesaleToggleLabel = translate(
    wholesaleCountrySpotConfig.wholesaleForm?.wholesaleToggleLabel ?? WHOLESALE_TOGGLE_LABEL_DEFAULT
  )
  const networkGroupField = wholesaleCountrySpotConfig.wholesaleForm?.networkTariffGroupIds

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <BooleanInput source="tariffData.wholesale" label={wholesaleToggleLabel} disabled={disabled} />
      </Grid>
      <DependentInput dependsOn="tariffData.wholesale" resolve={(enabled) => enabled === true || enabled === 'true'}>
        <Grid item xs={12} md={6}>
          <SelectInput
            source="tariffData.wholesale_country_iso2"
            label={translate('Wholesale spot data country')}
            choices={translatedCountryChoices}
            disabled={disabled}
            className={classes.textInput}
            variant="outlined"
            options={{ InputLabelProps: { shrink: true } }}
          />
        </Grid>
        <DependentInput dependsOn="tariffData.wholesale_country_iso2" resolve={resolveShowAuWholesaleSpotSeries}>
          <Grid item xs={12} md={6}>
            <SelectInput
              source="tariffData.wholesale_spot_series"
              label={translate('Wholesale spot series')}
              helperText={translate(
                'Calendar: one wholesale CSV per the selected price year below. Synthetic: bundled aggregate curve (year field is set automatically for the API).'
              )}
              choices={translatedSpotSeriesChoices}
              disabled={disabled}
              className={classes.textInput}
              variant="outlined"
              options={{ InputLabelProps: { shrink: true } }}
            />
          </Grid>
        </DependentInput>
        <DependentInput
          dependsOn={['tariffData.wholesale_country_iso2', 'tariffData.wholesale_spot_series']}
          resolve={resolveShowWholesalePriceYear}
        >
          <Grid item xs={12} md={6}>
            <SelectInput
              source="tariffData.wholesale_year"
              label={translate('Wholesale price year')}
              choices={wholesalePriceYearChoices}
              disabled={disabled}
              className={classes.textInput}
              variant="outlined"
              options={{ InputLabelProps: { shrink: true } }}
            />
          </Grid>
        </DependentInput>
        {networkGroupField && (
          <Grid item xs={12}>
            {/* @ts-ignore react-admin ReferenceArrayInput generic resource typing */}
            <ReferenceArrayInput
              source="tariffData.wholesale_network_tariff_group_ids"
              reference="network_tariff_groups"
              label={translate(networkGroupField.label)}
              sort={{ field: 'code', order: 'ASC' }}
              perPage={20}
              filter={networkTariffGroupListFilter}
              defaultValue={[]}
              fullWidth
            >
              <ChipsInput
                source="tariffData.wholesale_network_tariff_group_ids"
                optionText={networkTariffGroupOptionText}
                optionValue="id"
                translateChoice={false}
                disabled={disabled}
                fullWidth
                variant="outlined"
                className={classes.textInput}
                showSpinnerWhenRefreshing
                options={{
                  InputLabelProps: { shrink: true },
                  placeholder: translate('Search network tariffs...'),
                }}
              />
            </ReferenceArrayInput>
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
              {translate(networkGroupField.helperText)}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <NumberInput
            source="tariffData.wholesale_marginal_price_limits.import_min"
            label={translate('Import (buy) marginal min ($/kWh)')}
            helperText={translate(
              'Optional floor on combined import $/kWh after spot and network usage adders.'
            )}
            disabled={disabled}
            className={classes.textInput}
            variant="outlined"
            options={{ InputLabelProps: { shrink: true } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NumberInput
            source="tariffData.wholesale_marginal_price_limits.import_max"
            label={translate('Import (buy) marginal max ($/kWh)')}
            helperText={translate('Optional cap on combined import $/kWh.')}
            disabled={disabled}
            className={classes.textInput}
            variant="outlined"
            options={{ InputLabelProps: { shrink: true } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NumberInput
            source="tariffData.wholesale_marginal_price_limits.export_min"
            label={translate('Export (sell) marginal min ($/kWh)')}
            helperText={translate('Optional floor on combined export / feed-in $/kWh.')}
            disabled={disabled}
            className={classes.textInput}
            variant="outlined"
            options={{ InputLabelProps: { shrink: true } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NumberInput
            source="tariffData.wholesale_marginal_price_limits.export_max"
            label={translate('Export (sell) marginal max ($/kWh)')}
            helperText={translate('Optional cap on combined export $/kWh.')}
            disabled={disabled}
            className={classes.textInput}
            variant="outlined"
            options={{ InputLabelProps: { shrink: true } }}
          />
        </Grid>
      </DependentInput>
    </Grid>
  )
}

export default WholesaleTariffFields
